import type { components } from 'store/apis/permissionsApi/generated/permissions-api';

/**
 * The permission vocabulary.
 *
 * Exactly `permissions-service`'s catalogue, and nothing else. The app does not invent actions: a
 * capability the catalogue cannot express is added to the service (and enforced by the backend)
 * first, then appears here. A rollout restriction - "staff only, for now" - is a feature flag
 * (`useFeatureFlag`) asked beside the permission, never an action.
 *
 * `natter:*` covers every event kind - hosted natters, open natters and Natalie. Create and edit
 * both collapse into `natter:manage`.
 */

/**
 * The published catalogue, pinned to the service's own contract.
 *
 * Names come from the vendored spec at `api-specs/permissions.openapi.yaml`, which is authoritative
 * over the README (ADR-0006). The assertions below fail `type-check` when this list and the spec
 * disagree, so a rename upstream is a build error here rather than a 400 once the adapter is
 * swapped.
 *
 * The spec is the contract, not the deployment: `GET /v1/actions` serves the live catalogue, needs
 * SigV4, and has still not been read, so a service running behind its own spec would not show up
 * here. The U1 probe on HNS-323 owns that gap.
 */
export const CATALOGUE_ACTIONS = [
  'organisation:view',
  'organisation:manage',
  'community:view',
  'community:manage',
  'community:setEntitlements',
  'natter:view',
  'natter:participate',
  'natter:manage',
  'report:view',
  'report:manage',
] as const;

export type CatalogueAction = (typeof CATALOGUE_ACTIONS)[number];
/** An alias kept so call sites read as "an action" rather than as a statement about the catalogue. */
export type Action = CatalogueAction;

/**
 * The grant list the service serves us, read off its spec rather than restated.
 *
 * Deliberately `MeResponse.allowed` and not the `/v1/actions` catalogue, even though the spec calls
 * the latter authoritative: this app only ever reads the former, so it is the union a drift would
 * actually reach us through. The spec declares the same enum in three places, and pinning against a
 * copy we never parse would fail our build over an upstream inconsistency we neither suffer from
 * nor can fix.
 */
type ServedAction = components['schemas']['MeResponse']['allowed'][number];

/** `T` must be `true`. A `false` here is a catalogue drift; the alias name says which direction. */
type Assert<T extends true> = T;

/**
 * Both directions, because they fail differently. An action the service serves that this list omits
 * is a grant silently dropped by `transformResponse` - a capability the user holds and does not get.
 * One this list carries that the service no longer serves is an ordinary denial today, and a
 * rename's tell: the old name stops appearing while the new one is discarded as unknown (R20).
 */
type _EveryServedActionIsInTheCatalogue = Assert<ServedAction extends CatalogueAction ? true : false>;
type _EveryCatalogueActionIsServed = Assert<CatalogueAction extends ServedAction ? true : false>;

/** Every action, so a test can pin the whole vocabulary and a new one cannot slip in unnoticed. */
export const ALL_ACTIONS: readonly Action[] = CATALOGUE_ACTIONS;
